// WACC 탭 — 바닐라 dcf.html #tab-wacc / dcf.js 의
// toggleWaccPeer·onWaccTabDirectInput(직접입력), onPeerSearch·addPeer·removePeer·
// renderPeerChips(_loadPeerUniverse 포함, 직접 선정), lookupPeers·renderPeerResult
// (자동 선정 — POST /agent/peer/select/async → GET /agent/peer/status/{task_id} 폴링),
// buildWaccPayload·computeWacc·renderWacc·waccExcel·applyWaccResult 이식.
//
// 산출 흐름: WACC 산출(POST /agent/wacc) → 결과 표 렌더 → "이 WACC를 할인율로 적용"
// 버튼이 payload 에 peer/wacc_input 을 기록(store.applyWaccModule)하고 재계산한다.
import { useEffect, useRef, useState } from "react";
import { api, authHeaders } from "../../lib/api";
import { triggerDownload } from "../../lib/download";
import "../../styles/wacc.css";
import { parseNum, pct, todayStr } from "../../lib/format";
import { useT } from "../../lib/i18n";
import { Button } from "../../ui/Button";
import { CompanyLogo } from "../../ui/CompanyLogo";
import { useDcf } from "../../stores/dcf";
import { peerExcel, peerPdf } from "../../lib/labApi";
import { libViewUrl } from "../../lib/library";
import { PEER_STEP_TITLES, buildStepRows } from "../../lib/labSteps";
import { saveWaccEvaluation, waccSnapshot, type WaccEvaluation } from "../../lib/waccHistory";
import type { SavedEvaluation } from "../../lib/rcps/model";
import { resolvePeerInput } from "../../lib/waccPeers";
import type {
  DcfPayload,
  PeerKeyword,
  PeerSelectResult,
  WaccComputeBody,
  WaccResult,
} from "../../types/dcf";

interface OkResp {
  ok: boolean;
  message?: string;
}

// ── 종목 유니버스 (바닐라 _loadPeerUniverse — 모듈 레벨 1회 캐시) ──
interface TickerItem {
  symbol: string;
  name: string;
}
let universeCache: TickerItem[] | null = null;
let universeLoading: Promise<TickerItem[]> | null = null;

async function loadPeerUniverse(): Promise<TickerItem[]> {
  if (universeCache) return universeCache;
  if (universeLoading) return universeLoading;
  universeLoading = (async () => {
    try {
      const d = await api<
        OkResp & {
          kr?: Array<{ c: string; n: string }>;
          us?: Array<{ c: string; n?: string; en?: string }>;
        }
      >("/agent/ticker-universe");
      if (!d.ok) return [];
      const kr = (d.kr || []).map((it) => ({ symbol: "KRX:" + it.c, name: it.n }));
      const us = (d.us || []).map((it) => ({ symbol: it.c, name: it.n || it.en || it.c }));
      universeCache = [...kr, ...us];
    } catch {
      universeCache = [];
    }
    return universeCache;
  })();
  return universeLoading;
}

function fix3(x: number | null | undefined): string {
  return x != null && !isNaN(x) ? Number(x).toFixed(3) : "—";
}

function kwText(k: PeerKeyword): string {
  return typeof k === "string" ? k : k.keyword || "";
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

interface LogLine {
  step: string;
  cls: "" | "running" | "done";
}

function markLastDone(lines: LogLine[]): LogLine[] {
  if (!lines.length) return lines;
  const out = [...lines];
  out[out.length - 1] = { ...out[out.length - 1], cls: "done" };
  return out;
}

/** GET /agent/peer/status/{task_id} 응답 (바닐라 lookupPeers 폴링 소비 필드) */
interface PeerStatusResp {
  ok?: boolean;
  not_found?: boolean;
  done?: boolean;
  step?: string;
  message?: string;
  result?: PeerSelectResult;
}

/** 경과 mm:ss. tick 은 매초 바뀌는 값 — 인자로 받아야 리렌더가 걸린다. */
function elapsedLabel(startedAt: number, tick: number): string {
  const sec = Math.max(0, Math.floor(((tick || Date.now()) - startedAt) / 1000));
  return `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;
}

function Row({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="wacc-row">
      <span className="wacc-label">{label}</span>
      {children}
    </div>
  );
}

/** DCF adapter: only this wrapper reads or writes a DCF project. */
export default function WaccTab({ onApplied }: { onApplied?: () => void }) {
  const current = useDcf((s) => s.current);
  const setDiscountRateDirect = useDcf((s) => s.setDiscountRateDirect);
  const applyWaccModule = useDcf((s) => s.applyWaccModule);
  const setBusy = useDcf((s) => s.setBusy);
  const recalc = useDcf((s) => s.recalc);
  if (!current) return null;
  return <WaccCalculator payload={current.payload} onBusy={setBusy}
    onDiscountChange={setDiscountRateDirect} onApply={(body) => {
      applyWaccModule(body);
      void recalc();
      onApplied?.();
    }} />;
}

/** Shared calculation UI. Standalone mode has no dependency on DCF store state. */
export function WaccCalculator({ payload = {}, standalone = false, initialSaved, onBusy, onApply, onDiscountChange }: {
  payload?: DcfPayload;
  standalone?: boolean;
  initialSaved?: SavedEvaluation<WaccEvaluation>;
  onBusy?: (message: string | null) => void;
  onApply?: (body: WaccComputeBody) => void;
  onDiscountChange?: (value: number) => void;
}) {
  const { t, tf } = useT();
  const restored = standalone ? initialSaved?.payload.form : undefined;
  const [targetName, setTargetName] = useState(restored?.targetName ?? "");
  const [valuationDate, setValuationDate] = useState(() => restored?.valuationDate ?? todayStr());
  const p: DcfPayload = standalone
    ? { target_name: targetName.trim(), valuation_date: valuationDate, peer_input_method: "direct" }
    : payload;
  const [busy, setLocalBusy] = useState<string | null>(null);
  const [error, setError] = useState("");
  const setBusy = (message: string | null) => {
    setLocalBusy(message);
    onBusy?.(message);
  };
  const wi = (p?.wacc_input || {}) as Record<string, unknown>;

  // ── 선정 방식 + 파라미터 (바닐라 fillForm 의 WACC 부분 초기화 규칙) ──
  const [method, setMethod] = useState<string>(restored?.method ?? p?.peer_input_method ?? "value");
  const [peerPicks, setPeerPicks] = useState<TickerItem[]>(() =>
    restored ? restored.peerPicks : p?.peer_symbols && p.peer_symbols.length
      ? p.peer_symbols.map((s) => ({ symbol: s, name: s }))
      : [
          { symbol: "KRX:005930", name: "삼성전자" },
          { symbol: "KRX:000660", name: "SK하이닉스" },
        ],
  );
  // 사업내용 — WACC 자동선정용. Assumptions 탭의 사업내용(business_description)과
  // 이름이 달라 서로 안 이어지던 문제: 여기가 비어 있으면 그쪽 값으로 채운다.
  const [bizText, setBizText] = useState<string>(
    restored?.bizText ?? p?.business_text ?? p?.business_description ?? "",
  );
  useEffect(() => {
    // Assumptions 에서 나중에 입력해도 반영 — 단 사용자가 이 칸에 직접 쓴 건 지키지 않는다
    const fromAssumptions = p?.business_text || p?.business_description || "";
    if (fromAssumptions && !bizText.trim()) setBizText(fromAssumptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p?.business_text, p?.business_description]);
  const [topk, setTopk] = useState(restored?.topk ?? "10");
  const [rfTenor, setRfTenor] = useState(restored?.rfTenor ?? "10");
  const [rfCustom, setRfCustom] = useState(restored?.rfCustom ?? "3.0");
  const [kdRating, setKdRating] = useState<string>(
    restored?.kdRating ?? (typeof wi.kd_credit_rating === "string" ? wi.kd_credit_rating : "A0"),
  );
  const [kdTenor, setKdTenor] = useState(restored?.kdTenor ?? "5");
  const [kdCustom, setKdCustom] = useState(restored?.kdCustom ?? "5.5");
  const [mrp, setMrp] = useState(
    restored?.mrp ?? (typeof wi.mrp === "number" ? (wi.mrp * 100).toFixed(1) : "8.0"),
  );
  const [size, setSize] = useState<string>(
    restored?.size ?? (typeof wi.size_premium_bucket === "string" ? wi.size_premium_bucket : "micro"),
  );
  const [sizeCustom, setSizeCustom] = useState(restored?.sizeCustom ?? "0.0");
  const [tax2, setTax2] = useState(() => {
    const t = typeof wi.tax_rate === "number" ? wi.tax_rate : (p?.tax_rate ?? 0.22);
    return restored?.tax2 ?? (t * 100).toFixed(1);
  });
  const [betaFreq, setBetaFreq] = useState(restored?.betaFreq ?? "weekly");
  const [betaWin, setBetaWin] = useState(restored?.betaWin ?? "2.0");
  const [agg, setAgg] = useState(restored?.agg ?? "mean");

  // ── Peer 검색 드롭다운 ──
  const [searchQ, setSearchQ] = useState("");
  const [ddItems, setDdItems] = useState<TickerItem[] | null>(null); // null = 닫힘
  const [ddLoading, setDdLoading] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const searchVersion = useRef(0);

  // ── 자동 선정 (유사기업 조회) 진행 로그·결과 ──
  const [log, setLog] = useState<LogLine[]>([]);
  const [peerResult, setPeerResult] = useState<PeerSelectResult | null>(restored?.peerResult ?? null);
  const [lookupRunning, setLookupRunning] = useState(false);
  // 경과 시간 — 수십 초가 걸리는데 시간이 안 보이면 멈춘 것처럼 읽힌다
  const [lookupStartedAt, setLookupStartedAt] = useState(0);
  // 단계별 최초 수신 시각 — 소요 계산의 근거
  const [logSeenAt, setLogSeenAt] = useState<Record<string, number>>({});
  const [lookupTick, setLookupTick] = useState(0);
  useEffect(() => {
    if (!lookupRunning) return;
    const t = setInterval(() => setLookupTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [lookupRunning]);
  // 조회로 확보한 peer — 사업내용이 그대로면 WACC 산출 시 direct 로 전환해 재선정을 건너뛴다
  const autoPeer = useRef<{ symbols: string[]; biz: string }>({ symbols: [], biz: "" });

  // ── 선정 결과 파일(엑셀·PDF) ──
  const [peerFileBusy, setPeerFileBusy] = useState("");
  const [peerFileErr, setPeerFileErr] = useState("");

  // ── 산출 결과 ──
  const [waccResult, setWaccResult] = useState<WaccResult | null>(standalone ? initialSaved?.payload.result ?? null : null);
  const [snapshot, setSnapshot] = useState<WaccEvaluation | null>(standalone ? initialSaved?.payload ?? null : null);
  const [saveId, setSaveId] = useState(initialSaved?.id ?? "");
  const [saved, setSaved] = useState(!!initialSaved);
  const [saveError, setSaveError] = useState("");
  const clearResult = () => {
    setWaccResult(null);
    setSnapshot(null);
    setSaved(false);
    setSaveError("");
  };
  const [excelNote, setExcelNote] = useState<{ url?: string; err?: string } | null>(null);

  const fillSample = () => {
    if (busy || lookupRunning || peerFileBusy) return;
    // Reset every calculation condition and discard results from the previous inputs.
    setTargetName("샘플회사 (예시)");
    setValuationDate(todayStr());
    setMethod("direct");
    setPeerPicks([
      { symbol: "KRX:005930", name: "삼성전자" },
      { symbol: "KRX:000660", name: "SK하이닉스" },
    ]);
    setBizText("");
    setTopk("10");
    setRfTenor("10");
    setRfCustom("3.0");
    setKdRating("A0");
    setKdTenor("5");
    setKdCustom("5.5");
    setMrp("8.0");
    setSize("micro");
    setSizeCustom("0.0");
    setTax2("22.0");
    setBetaFreq("weekly");
    setBetaWin("2.0");
    setAgg("mean");
    clearTimeout(searchTimer.current);
    searchVersion.current++;
    setSearchQ("");
    setDdItems(null);
    setDdLoading(false);
    autoPeer.current = { symbols: [], biz: "" };
    setPeerResult(null);
    setLog([]);
    setLookupStartedAt(0);
    setLogSeenAt({});
    setPeerFileErr("");
    clearResult();
    setExcelNote(null);
    setError("");
  };

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // ── 검색 (바닐라 onPeerSearch — 100ms debounce, 최대 12건, 선택된 종목 제외) ──
  const onSearchChange = (q: string) => {
    const version = ++searchVersion.current;
    setSearchQ(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (q.trim().length < 1) {
      setDdItems(null);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setDdLoading(true);
      setDdItems([]);
      const universe = await loadPeerUniverse();
      if (!alive.current || version !== searchVersion.current) return;
      const qLow = q.trim().toLowerCase();
      setDdItems(
        universe
          .filter(
            (it) =>
              it.name.toLowerCase().includes(qLow) || it.symbol.toLowerCase().includes(qLow),
          )
          .filter((it) => !peerPicks.some((x) => x.symbol === it.symbol))
          .slice(0, 12),
      );
      setDdLoading(false);
    }, 100);
  };
  const addPeer = (it: TickerItem) => {
    if (busy || lookupRunning) return;
    clearResult();
    setExcelNote(null);
    setPeerPicks((prev) =>
      prev.some((x) => x.symbol === it.symbol) ? prev : [...prev, it],
    );
    setSearchQ("");
    setDdItems(null);
  };
  const removePeer = (sym: string) => {
    clearResult();
    setExcelNote(null);
    setPeerPicks((prev) => prev.filter((x) => x.symbol !== sym));
  };

  // ── 유사기업 자동선정 (바닐라 lookupPeers — 비동기 시작 + 2초 폴링) ──
  const lookupPeers = async () => {
    const biz = bizText.trim();
    if (biz.length < 10) {
      setError("사업내용을 10자 이상 입력하세요.");
      return;
    }
    const payload = {
      business_text: biz,
      top_k: parseInt(topk, 10) || 10,
      valuation_date: p.valuation_date,
    };
    setPeerResult(null);
    clearResult();
    setExcelNote(null);
    setError("");
    setLookupRunning(true);
    setLookupStartedAt(Date.now());
    setLog([{ step: "유사기업 선정 시작…", cls: "running" }]);
    setLogSeenAt({});
    try {
      const start = await api<OkResp & { task_id?: string }>("/agent/peer/select/async", {
        method: "POST",
        body: JSON.stringify({ payload }),
      });
      if (!start.ok || !start.task_id)
        throw new Error(start.message || "선정 작업을 시작하지 못했습니다.");
      const taskId = start.task_id;
      let lastStep = "";
      const deadline = Date.now() + 5 * 60 * 1000;
      let notFoundStreak = 0;
      let netFailStreak = 0;
      for (;;) {
        if (!alive.current) return;
        if (Date.now() > deadline) throw new Error("시간 내에 완료되지 않았습니다.");
        await sleep(2000);
        // 폴링은 raw fetch — 정상 대기(not_found)와 실제 오류를 구분해야 한다 (바닐라 동일)
        let st: PeerStatusResp;
        try {
          const resp = await fetch("/agent/peer/status/" + encodeURIComponent(taskId), {
            headers: authHeaders(),
          });
          st = (await resp.json()) as PeerStatusResp;
          netFailStreak = 0;
        } catch {
          if (++netFailStreak > 5) throw new Error("연결이 불안정합니다.");
          continue;
        }
        if (st.not_found) {
          if (++notFoundStreak > 15) throw new Error("작업을 찾을 수 없습니다.");
          continue;
        }
        if (!st.ok) throw new Error(st.message || "선정 중 오류가 발생했습니다.");
        notFoundStreak = 0;
        if (st.done) {
          const result = st.result || {};
          autoPeer.current = {
            symbols: (result.peers || [])
              .map((x) => x.symbol)
              .filter((s): s is string => !!s),
            biz: bizText,
          };
          // 선정 결과를 직접 선정과 같은 칩 목록에 넣는다 — 여기서 빼거나 더한 것이
          // 그대로 WACC 산출에 쓰인다(buildBody 가 peerPicks 를 본다).
          setPeerPicks(
            (result.peers || [])
              .filter((x) => !!x.symbol)
              .map((x) => ({ symbol: String(x.symbol), name: String(x.name || x.symbol) })),
          );
          setLog((prev) => markLastDone(prev));
          setPeerResult(result);
          return;
        }
        const step = st.step || "";
        if (step && step !== lastStep) {
          lastStep = step;
          setLog((prev) => [...markLastDone(prev), { step, cls: "running" }]);
          setLogSeenAt((prev) => (prev[step] ? prev : { ...prev, [step]: Date.now() }));
        }
      }
    } catch (e) {
      setLog((prev) => [...prev, { step: "오류: " + errMsg(e), cls: "" }]);
    } finally {
      if (alive.current) setLookupRunning(false);
    }
  };

  // 검색 + 칩 — 직접 선정과 자동 선정 결과 편집이 같은 UI 를 쓴다.
  // 칩 목록(peerPicks)이 곧 WACC 산출에 넘어가는 종목이라, 화면에 보이는 것과
  // 계산에 쓰인 것이 어긋날 수 없다.
  const peerPicker = (label: React.ReactNode, hint?: React.ReactNode) => (
    <div className="wacc-row" style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
      <span className="wacc-label">
        {label} {hint ? <span className="hint">{hint}</span> : null}
      </span>
      <div style={{ position: "relative" }}>
        <input
          className="wacc-input"
          placeholder={t("ph.peer_search")}
          style={{ width: "100%", textAlign: "left" }}
          autoComplete="off"
          value={searchQ}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") setDdItems(null); }}
        />
        {ddItems !== null && (
          <div className="peer-dd">
            {ddLoading ? (
              <div className="peer-dd-empty">{t("dd.searching")}</div>
            ) : ddItems.length ? (
              ddItems.map((it) => (
                <div key={it.symbol} className="peer-dd-item" onClick={() => addPeer(it)}>
                  <span className="nm">
                    <CompanyLogo symbol={it.symbol} name={it.name} size="sm" />
                    {it.name}
                  </span>
                  <span className="sym">{it.symbol}</span>
                </div>
              ))
            ) : (
              <div className="peer-dd-empty">{t("dd.no_results")}</div>
            )}
          </div>
        )}
      </div>
      <div className="file-chips">
        {peerPicks.map((x) => (
          <span key={x.symbol} className="peer-chip">
            <CompanyLogo symbol={x.symbol} name={x.name} size="xs" />
            {x.name}{" "}
            <span className="sym" style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--text-secondary)" }}>
              {x.symbol}
            </span>{" "}
            <button type="button" onClick={() => removePeer(x.symbol)}>✕</button>
          </span>
        ))}
      </div>
    </div>
  );

  // 진행 로그 → 6단계 표. 안 온 단계도 자리를 지킨다(캐시 재사용).
  const stepRows = buildStepRows(
    log.map((l) => l.step), logSeenAt, lookupRunning, lookupTick, PEER_STEP_TITLES,
  );

  // 선정 결과를 파일로 — 유사기업 페이지와 같은 엔드포인트를 쓴다.
  // 칩을 편집했어도 근거(선정 사유·키워드)는 조회 결과에 있으므로 원본을 넘긴다.
  const makePeerFile = async (kind: "excel" | "pdf") => {
    if (!peerResult) return;
    setPeerFileBusy(kind);
    setPeerFileErr("");
    try {
      const payload = { business_text: bizText.trim(), result: peerResult };
      const url = kind === "excel" ? await peerExcel(payload) : await peerPdf(payload);
      window.open(kind === "pdf" ? libViewUrl(url) : url, "_blank", "noopener");
    } catch (e) {
      setPeerFileErr(errMsg(e));
    } finally {
      setPeerFileBusy("");
    }
  };

  // ── WACC 산출 body (바닐라 buildWaccPayload) ──
  const buildBody = (): WaccComputeBody => {
    const body: WaccComputeBody = {
      target_name: p.target_name || "평가대상",
      valuation_date: p.valuation_date,
      peer_input_method: method,
      mrp: parseNum(mrp) / 100,
      size_premium_bucket: size,
      tax_rate: parseNum(tax2) / 100,
      beta_frequency: betaFreq,
      beta_window_years: parseNum(betaWin) || 2.0,
      aggregation: agg,
    };
    if (rfTenor === "custom") {
      body.rf_tenor = "10";
      body.rf_override = parseNum(rfCustom) / 100;
    } else {
      body.rf_tenor = rfTenor;
    }
    body.kd_tenor = kdTenor;
    if (kdRating === "custom") {
      body.kd_credit_rating = "BBB-"; // 백엔드 validator 통과용 더미 (kd_override 가 우선)
      body.kd_override = parseNum(kdCustom) / 100;
    } else {
      body.kd_credit_rating = kdRating;
    }
    if (size === "custom") body.size_premium_override = parseNum(sizeCustom) / 100;
    // 화면의 칩이 곧 계산 대상 — 규칙은 lib/waccPeers 에 모아 테스트로 고정했다.
    // "value"(직접입력)는 여기서 제외해야 한다 — 예전엔 캐스팅으로 밀어넣어
    // peer_input_method 가 auto/direct 로 덮어써졌고, 그래서 직접입력인데
    // "사업내용 10자" 오류가 나거나 계산이 실제로 돌아갔다.
    if (method !== "value") {
      Object.assign(
        body,
        resolvePeerInput(method as "direct" | "auto", peerPicks, peerResult !== null,
                         bizText, parseInt(topk, 10) || 10),
      );
    }
    return body;
  };

  const validateBody = (body: WaccComputeBody): boolean => {
    setError("");
    if (standalone && !body.valuation_date) {
      setError("평가기준일을 입력하세요.");
      return false;
    }
    if (body.peer_input_method === "value") {
      setError(
        '"WACC 직접입력" 모드에서는 산출이 필요 없습니다. 값을 입력하면 바로 할인율로 사용됩니다.',
      );
      return false;
    }
    if (
      body.peer_input_method === "direct" &&
      !(body.peer_symbols && body.peer_symbols.length)
    ) {
      setError("유사기업을 선택하세요.");
      return false;
    }
    if (body.peer_input_method === "auto" && (body.business_text || "").length < 10) {
      setError("사업내용을 10자 이상 입력하세요.");
      return false;
    }
    return true;
  };

  const computeWacc = async () => {
    const body = buildBody();
    if (!validateBody(body)) return;
    setDdItems(null);
    const preSelected =
      method === "auto" && peerPicks.length > 0 && body.peer_input_method === "direct";
    setBusy(
      preSelected || method === "direct"
        ? "선정된 유사기업으로 베타 회귀·WACC 산출 중입니다..."
        : "유사기업 선정·베타 회귀·WACC 산출 중입니다... (수십 초 소요될 수 있습니다. 먼저 「유사기업 조회」로 미리 선정하면 더 빠릅니다.)",
    );
    try {
      const d = await api<OkResp & { result?: WaccResult }>("/agent/wacc", {
        method: "POST",
        body: JSON.stringify({ payload: body }),
      });
      if (!d.ok || !d.result) throw new Error(d.message || "백엔드 오류");
      if (!alive.current) return;
      setWaccResult(d.result);
      if (standalone) {
        setSnapshot(waccSnapshot({ targetName, valuationDate, method, peerPicks, bizText, topk,
          rfTenor, rfCustom, kdRating, kdTenor, kdCustom, mrp, size, sizeCustom, tax2,
          betaFreq, betaWin, agg, peerResult }, body, d.result));
        setSaveId(crypto.randomUUID().replaceAll("-", ""));
        setSaved(false);
        setSaveError("");
      }
    } catch (e) {
      setError("WACC 산출 실패: " + errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const saveResult = async () => {
    if (!snapshot || !saveId || busy || saved) return;
    setSaveError("");
    setBusy("WACC 결과를 저장 중입니다…");
    try {
      await saveWaccEvaluation(snapshot, saveId);
      if (alive.current) setSaved(true);
    } catch (e) {
      if (alive.current) setSaveError("저장 실패: " + errMsg(e));
    } finally {
      if (alive.current) setBusy(null);
    }
  };

  const waccExcel = async () => {
    const body = buildBody();
    setExcelNote(null);
    if (!validateBody(body)) return;
    setBusy("WACC 엑셀을 생성 중입니다...");
    try {
      const d = await api<OkResp & { url?: string }>("/agent/wacc/excel", {
        method: "POST",
        body: JSON.stringify({ payload: body }),
      });
      if (!d.ok) throw new Error(d.message || "백엔드 오류");
      if (d.url) {
        triggerDownload(d.url);
        setExcelNote({ url: d.url });
      } else {
        setExcelNote({ err: "URL을 받지 못했습니다." });
      }
    } catch (e) {
      setExcelNote({ err: "WACC 엑셀 생성 실패: " + errMsg(e) });
    } finally {
      setBusy(null);
    }
  };

  // ── 산출 WACC 를 할인율로 적용 (바닐라 applyWaccResult) ──
  const applyWacc = () => {
    const w = waccResult?.wacc?.value;
    if (w == null) {
      alert("먼저 WACC를 산출하세요.");
      return;
    }
    onApply?.(buildBody());
  };

  // ── 렌더 ──
  const kw = peerResult?.keywords || {};
  const peers = peerResult?.peers || [];
  const rw = waccResult;
  const cs = rw?.capital_structure || {};
  const inp = rw?.inputs || {};
  const ib = rw?.industry_beta || {};
  const rf = rw?.rf || {};
  const kd = rw?.kd || {};
  const ke = rw?.ke || {};
  const meta = rw?.meta || {};
  const resultRows: Array<[string, React.ReactNode]> = rw
    ? [
        ["WACC", pct(rw.wacc?.value)],
        [t("wr.ke"), pct(ke.value)],
        [t("wr.kd_at"), pct(kd.after_tax)],
        [t("wr.kd_pre"), `${pct(kd.pre_tax)} (${kd.rating || ""})`],
        ["Beta (levered)", fix3(ib.levered_target)],
        ["Beta (levered, raw)", fix3(ib.levered_target_raw)],
        ["Beta (unlevered)", fix3(ib.unlevered)],
        [t("wr.rf"), `${pct(rf.value)} (${rf.tenor ?? ""}Y)`],
        ["MRP", pct(inp.mrp)],
        ["Size Premium", pct(inp.size_premium)],
        ["D/E", fix3(cs.d_e)],
        ["E/V", pct(cs.e_v)],
        ["D/V", pct(cs.d_v)],
        [t("wr.peer_count"), meta.peer_count != null ? meta.peer_count : "—"],
        [t("wr.agg"), meta.aggregation === "mean" ? "평균" : meta.aggregation === "median" ? "중앙값" : meta.aggregation || "—"],
      ]
    : [];

  return (
    <>
      {standalone && <div className="rcps-heading wacc-heading">
        <div>
          <span className="rcps-eyebrow">COST OF CAPITAL</span>
          <h1>WACC 계산</h1>
          <p>유사기업의 베타와 자본구조를 바탕으로 가중평균자본비용을 산출합니다.</p>
        </div>
        <div className="wacc-heading-actions">
          <a className="report-btn" href="/rcps/evaluation/result?kind=wacc">저장 이력</a>
          <Button disabled={!!busy || lookupRunning || !!peerFileBusy} onClick={fillSample}
            title="삼성전자·SK하이닉스와 예시 WACC 계산 조건을 입력합니다">샘플 입력</Button>
        </div>
      </div>}
      <fieldset className="wacc-fields" disabled={!!busy || lookupRunning} onChangeCapture={() => {
        clearResult();
        setExcelNote(null);
        setError("");
      }}>
      {standalone && <div className="data-card">
        <h4>01 평가 기준</h4>
        <Row label={<label htmlFor="wacc-target">대상회사명 (선택)</label>}>
          <input id="wacc-target" className="wacc-input" placeholder="평가대상" value={targetName}
            onChange={(e) => setTargetName(e.target.value)} />
        </Row>
        <Row label={<label htmlFor="wacc-date">평가기준일</label>}>
          <input id="wacc-date" className="wacc-input" type="date" value={valuationDate}
            onChange={(e) => setValuationDate(e.target.value)} />
        </Row>
      </div>}
      <div className="data-card">
        <h4>
          {standalone ? "02 유사기업 선정" : t("h.peer_selection")} <span className="hint">{t("hint.peer_basis")}</span>
        </h4>
        <Row label={t("lbl.peer_method")}>
          <select
            className="wacc-input"
            style={{ width: 200 }}
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          >
            {!standalone && <option value="value">{t("opt.wacc_value")}</option>}
            <option value="direct">{t("opt.wacc_direct")}</option>
            <option value="auto">{t("opt.wacc_auto")}</option>
          </select>
        </Row>
        {method === "value" && (
          <Row
            label={
              <>
                {t("lbl.wacc_direct")}{" "}
                <span className="hint">{t("hint.wacc_direct")}</span>
              </>
            }
          >
            <input
              key={((p.discount_rate || 0) * 100).toFixed(2)}
              className="wacc-input"
              style={{ width: 90 }}
              defaultValue={((p.discount_rate || 0) * 100).toFixed(2)}
              onBlur={(e) => {
                const v = parseNum(e.target.value) / 100;
                if (v !== (p.discount_rate || 0)) onDiscountChange?.(v);
              }}
            />
          </Row>
        )}
        {method === "direct" && peerPicker(t("lbl.peer_tickers"), t("hint.peer_search"))}
        {method === "auto" && (
          <div>
            <div className="wacc-row" style={{ alignItems: "flex-start" }}>
              <span className="wacc-label">{t("lbl.biz_text")}</span>
              <textarea
                className="wacc-input"
                placeholder={t("ph.biz_text")}
                style={{ width: 300, height: 64, textAlign: "left", resize: "vertical" }}
                value={bizText}
                onChange={(e) => setBizText(e.target.value)}
              />
            </div>
            <Row label={t("lbl.topk")}>
              <input
                className="wacc-input"
                value={topk}
                onChange={(e) => setTopk(e.target.value)}
              />
            </Row>
            <div style={{ marginTop: 10 }}>
              <Button variant="default" disabled={lookupRunning} onClick={() => void lookupPeers()}>
                {t("btn.peer_lookup")}
              </Button>
              <span className="hint" style={{ marginLeft: 8 }}>
                {t("hint.peer_lookup")}
              </span>
              {/* 조회 전에도 보여야 한다 — 결과가 나온 뒤에 알려주면 이미 늦다 */}
              <span className="hint peer-more-hint">
                보다 구체적인 유사기업 조회는{" "}
                <a href="/peer" target="_blank" rel="noreferrer">밸류에이전트 유사기업 조회</a>
                {" "}를 이용하세요.
              </span>
            </div>
            {log.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div className="peer-log-head">
                  <span>
                    {lookupRunning
                      ? `진행 중 (${stepRows.filter((r) => r.state === "done").length}/${stepRows.length})`
                      : `진행 완료 (${stepRows.length}단계)`}
                  </span>
                  {lookupStartedAt > 0 && (
                    <span className="pl-elapsed">
                      {elapsedLabel(lookupStartedAt, lookupTick)}
                      {lookupRunning ? " · 수십 초 걸립니다" : ""}
                    </span>
                  )}
                </div>
                {/* 6단계를 항상 세운다 — 캐시가 맞으면 백엔드가 2~3단계를 보내지
                    않아 1 다음 4가 튀어나오고, 단계가 사라진 것처럼 보였다. */}
                <div className="peer-log">
                  {stepRows.map((r) => (
                    <div key={r.no} className={`peer-log-line ${r.state}`}>
                      <span className="pl-no">{r.no}/{stepRows.length}</span>
                      <span className="pl-step">{r.text}</span>
                      {r.skipped && <span className="pl-skip">캐시 재사용</span>}
                      {r.secs != null && r.state === "done" && (
                        <span className="pl-secs">{r.secs}초</span>
                      )}
                      {/* 진행 중인 줄에만 움직이는 점 — 텍스트가 한동안 안 바뀌어도
                          살아 있다는 것이 보여야 한다(채팅의 stream-dot 과 같은 리듬) */}
                      {r.state === "now" && lookupRunning && (
                        <span className="pl-dots" aria-hidden="true">
                          <i /><i /><i />
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                {peerResult && (
                  <>
                    {/* 제외 키워드(negative)는 보여주지 않는다 — 선정에서 걸러낸
                        업종이라 결과와 무관하고, 빨간 태그가 '이 회사들이 나왔다'
                        로 오해된다. 근거가 필요하면 조서 엑셀·PDF 에 남는다. */}
                    {!!(kw.core_keywords?.length || kw.synonyms?.length) && (
                      <div style={{ marginTop: 10 }}>
                        <div className="hint" style={{ marginBottom: 4 }}>
                          {t("peer.keywords")}
                        </div>
                        <div className="peer-kw">
                          {(kw.core_keywords || []).map((k, i) => (
                            <span key={"c" + i} className="peer-kw-tag">
                              {kwText(k)}
                            </span>
                          ))}
                          {(kw.synonyms || []).map((k, i) => (
                            <span key={"s" + i} className="peer-kw-tag">
                              {kwText(k)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div
                      style={{
                        margin: "10px 0 6px",
                        fontSize: 13,
                        color: "var(--navy)",
                        fontWeight: 600,
                      }}
                    >
                      {tf("peer.selected_done", { n: peers.length })}
                    </div>
                    {/* 선정 결과를 칩으로 — 여기서 빼거나 회사명으로 더한 것이 그대로
                        WACC 산출에 쓰인다. 자동 선정 결과가 늘 맞진 않으므로 손댈
                        수 있어야 하고, 손댄 결과가 계산에 반영돼야 한다. */}
                    {peerPicker(
                      "WACC 에 쓸 유사기업",
                      `${peerPicks.length}개 — 칩의 ✕ 로 빼고, 아래 검색으로 회사를 더할 수 있습니다`,
                    )}
                    {/* 선정 근거를 조서로 남길 수 있어야 한다 — 유사기업 페이지와 같은 산출물 */}
                    <div className="peer-files">
                      <Button variant="default" disabled={!!peerFileBusy}
                              onClick={() => void makePeerFile("excel")}>
                        {peerFileBusy === "excel" ? "생성 중…" : "엑셀 파일로"}
                      </Button>
                      <Button variant="default" disabled={!!peerFileBusy}
                              onClick={() => void makePeerFile("pdf")}>
                        {peerFileBusy === "pdf" ? "생성 중…" : "선정 보고서 PDF"}
                      </Button>
                    </div>
                    {peerFileErr && (
                      <p className="peer-file-err" role="alert">파일 생성 실패: {peerFileErr}</p>
                    )}
                    {peers.map((pr, i) => {
                      const rem = pr.remarks || {};
                      const reason = rem["선정근거"] || rem["매출구조_분석"] || "";
                      const total = pr.composite?.total;
                      return (
                        <div key={i} className="peer-result-item">
                          <div className="pri-head">
                            <span>
                              <span className="pri-name">
                                {i + 1}. {pr.name || ""}
                              </span>{" "}
                              <span className="pri-sym">{pr.symbol || ""}</span>
                            </span>
                            {total != null && (
                              <span className="pri-score">
                                {t("peer.score")} {total.toFixed(2)}
                              </span>
                            )}
                          </div>
                          {reason && <div className="pri-reason">{reason}</div>}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="data-card">
        <h4>{standalone ? "03 WACC 산출 조건" : t("h.wacc_params")}</h4>
        <Row
          label={
            <>
              {t("lbl.rf")} <span className="hint">{t("hint.rf_kofia")}</span>
            </>
          }
        >
          <select
            className="wacc-input"
            style={{ width: 170 }}
            value={rfTenor}
            onChange={(e) => setRfTenor(e.target.value)}
          >
            {["1", "3", "5", "10"].map((n) => (
              <option key={n} value={n}>
                {tf("opt.rf_tenor", { n })}
              </option>
            ))}
            <option value="custom">{t("opt.custom")}</option>
          </select>
        </Row>
        {rfTenor === "custom" && (
          <Row label={t("lbl.rf_direct")}>
            <input
              className="wacc-input"
              style={{ width: 90 }}
              value={rfCustom}
              onChange={(e) => setRfCustom(e.target.value)}
            />
          </Row>
        )}
        <Row
          label={
            <>
              {t("lbl.kd")} <span className="hint">{t("hint.kd_kofia")}</span>
            </>
          }
        >
          <span style={{ display: "flex", gap: 6 }}>
            <select
              className="wacc-input"
              style={{ width: 140 }}
              value={kdRating}
              onChange={(e) => setKdRating(e.target.value)}
            >
              {["AAA", "AA+", "AA0", "AA-", "A+", "A0", "A-", "BBB+", "BBB0", "BBB-"].map(
                (x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ),
              )}
              <option value="custom">{t("opt.custom")}</option>
            </select>
            <select
              className="wacc-input"
              style={{ width: 80 }}
              value={kdTenor}
              onChange={(e) => setKdTenor(e.target.value)}
            >
              {["1", "2", "3", "5", "10"].map((x) => (
                <option key={x} value={x}>
                  {tf("opt.year_n", { n: x })}
                </option>
              ))}
            </select>
          </span>
        </Row>
        {kdRating === "custom" && (
          <Row
            label={
              <>
                {t("lbl.kd_direct")} <span className="hint">{t("hint.kd_pre_tax")}</span>
              </>
            }
          >
            <input
              className="wacc-input"
              style={{ width: 90 }}
              value={kdCustom}
              onChange={(e) => setKdCustom(e.target.value)}
            />
          </Row>
        )}
        <Row
          label={
            <>
              {t("lbl.mrp")} <span className="hint">{t("hint.mrp")}</span>
            </>
          }
        >
          <input
            className="wacc-input"
            style={{ width: 90 }}
            value={mrp}
            onChange={(e) => setMrp(e.target.value)}
          />
        </Row>
        <Row label={t("lbl.size_premium")}>
          <select
            className="wacc-input"
            style={{ width: 200 }}
            value={size}
            onChange={(e) => setSize(e.target.value)}
          >
            <option value="none">none (0%)</option>
            <option value="mid">mid (-0.36%)</option>
            <option value="low">low (+1.37%)</option>
            <option value="micro">micro (+4.02%)</option>
            <option value="custom">{t("opt.custom")}</option>
          </select>
        </Row>
        {size === "custom" && (
          <Row label={t("lbl.size_direct")}>
            <input
              className="wacc-input"
              style={{ width: 90 }}
              value={sizeCustom}
              onChange={(e) => setSizeCustom(e.target.value)}
            />
          </Row>
        )}
        <Row label={t("lbl.tax_rate")}>
          <input
            className="wacc-input"
            style={{ width: 90 }}
            value={tax2}
            onChange={(e) => setTax2(e.target.value)}
          />
        </Row>
        <Row label={t("lbl.beta")}>
          <span style={{ display: "flex", gap: 6 }}>
            <select
              className="wacc-input"
              style={{ width: 90 }}
              value={betaFreq}
              onChange={(e) => setBetaFreq(e.target.value)}
            >
              <option value="weekly">주간 (weekly)</option>
              <option value="monthly">월간 (monthly)</option>
            </select>
            <input
              className="wacc-input"
              style={{ width: 60 }}
              value={betaWin}
              onChange={(e) => setBetaWin(e.target.value)}
            />
          </span>
        </Row>
        <Row label={t("lbl.peer_agg")}>
          <select
            className="wacc-input"
            style={{ width: 170 }}
            value={agg}
            onChange={(e) => setAgg(e.target.value)}
          >
            <option value="mean">평균 (mean)</option>
            <option value="median">중앙값 (median)</option>
          </select>
        </Row>
      </div>

      {/* 직접입력(value) 모드에선 산출할 게 없다 — 입력한 값이 바로 할인율이므로
          버튼 자체를 숨긴다 (누르면 오류·불필요한 서버 계산이 나가던 문제) */}
      {method !== "value" && (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <Button variant="primary" onClick={() => void computeWacc()}>
          {t("btn.wacc_calc")}
        </Button>
        <Button variant="default" disabled={!!busy || lookupRunning} onClick={() => void waccExcel()}>
          {t("btn.wacc_excel")}
        </Button>
        {onApply && <Button variant="default" disabled={!waccResult} onClick={applyWacc}>
          {t("btn.apply_wacc")}
        </Button>}
      </div>
      )}
      </fieldset>
      {busy && <p className="wacc-status" role="status">{busy}</p>}
      {error && <p className="peer-file-err" role="alert">{error}</p>}
      {excelNote &&
        (excelNote.url ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 13,
              margin: "6px 0",
            }}
          >
            <span style={{ color: "var(--navy)", fontWeight: 600 }}>
              {t("msg.excel_started")}
            </span>
            <a
              className="dl-link"
              style={{ marginTop: 0, fontSize: 13 }}
              href={excelNote.url}
              target="_blank"
              rel="noopener noreferrer"
              download
            >
              {t("btn.download_again")}
            </a>
          </div>
        ) : (
          <span className="hint">{excelNote.err}</span>
        ))}

      {rw && (
        <div className="data-card">
          <h4>{t("h.wacc_result")}</h4>
          {standalone && <p className="hint">{p.target_name || "평가대상"} · 기준일 {p.valuation_date}</p>}
          <table className="data-table">
            <tbody>
              {resultRows.map(([label, val], i) => (
                <tr key={i}>
                  <td>{label}</td>
                  <td>{val}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 12 }}>
            {standalone && <Button variant="default" disabled={!!busy || saved || !snapshot} onClick={() => void saveResult()}>
              {saved ? "저장됨" : "결과 저장"}
            </Button>}{" "}
            <Button variant="default" disabled={!!busy || lookupRunning} onClick={() => void waccExcel()}>
              {t("btn.wacc_basis_excel")}
            </Button>{" "}
            <span className="hint">{t("hint.wacc_basis")}</span>
          </div>
          {standalone && saved && <p className="hint" role="status">계정에 저장했습니다. ‘저장 이력’에서 다시 불러올 수 있습니다.</p>}
          {saveError && <p className="peer-file-err" role="alert">{saveError}</p>}
          {!!(rw.peers && rw.peers.length) && (
            <>
              <h4 style={{ marginTop: 18 }}>{t("h.selected_peers")}</h4>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t("col.ticker")}</th>
                    <th>β(L)</th>
                    <th>β(U)</th>
                    <th>D/E</th>
                  </tr>
                </thead>
                <tbody>
                  {rw.peers.map((pe, i) => (
                    <tr key={i}>
                      <td><span className="wacc-company"><CompanyLogo symbol={pe.symbol || ""} name={pe.name_ko || pe.symbol || ""} size="xs" />{pe.name_ko || pe.symbol || ""} <span className="hint">{pe.symbol}</span></span></td>
                      <td>{fix3(pe.beta_levered)}</td>
                      <td>{fix3(pe.beta_unlevered)}</td>
                      <td>{fix3(pe.d_e)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </>
  );
}
